import { createApp } from 'vue';
import backdropClose from './directives/backdropClose.js';
import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import routes from './router';
import './styles.css';
import './assets/styles/variables.css';
import './assets/styles/components.css';
import './assets/styles/element-plus-theme.css';
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import ko from 'element-plus/dist/locale/ko.mjs';
import axios from 'axios';
import { currentUser, updateUser } from './store.js';

// Axios 전역 설정: Authorization 헤더 자동 추가
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 세션 만료(401) 공통 처리 — axios/fetch 어느 경로든 동일하게 로그인으로 보낸다.
// 여러 요청이 동시에 401이 나도 알림/리다이렉트는 한 번만 실행.
let __sessionExpiredHandled = false;
function handleSessionExpired() {
  try { localStorage.removeItem('token'); } catch {}
  // 이미 로그인 페이지면(로그인 실패 등) 리다이렉트/알림 생략 → 루프 방지
  if (window.location.pathname === '/login' || window.location.pathname.endsWith('/login.html')) return;
  if (__sessionExpiredHandled) return;
  __sessionExpiredHandled = true;
  alert('세션이 만료되었습니다. 다시 로그인해주세요.');
  const redirect = window.location.pathname + window.location.search;
  window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
}

// 타임아웃 있는 fetch — 라우터 가드 프로브가 stale 커넥션에서 무한 대기(화면 먹통)하는 것을 방지.
async function fetchWithTimeout(url, opts = {}, ms = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Axios 응답 인터셉터: 401 처리
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) handleSessionExpired();
    return Promise.reject(error);
  }
);

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Global fetch wrapper: attach token and redirect on 401/403
const originalFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  const headers = new Headers(init.headers || {});
  const url = typeof input === 'string' ? input : input.url;
  const isAuthMe = (() => {
    try { const u = new URL(url, window.location.origin); return u.pathname === '/api/auth/me'; }
    catch { return false; }
  })();
  // Attach Authorization header if not already set
  if (!headers.has('Authorization')) {
    const token = localStorage.getItem('token');
    if (token) headers.set('Authorization', 'Bearer ' + token);
  }
  const finalInit = { credentials: 'include', ...init, headers };

  let res = await originalFetch(input, finalInit);

  // For any 401, if we sent Authorization, retry once without it (token may be stale while session is valid)
  if (res.status === 401 && headers.has('Authorization') && !isAuthMe) {
    const retryHeaders = new Headers(finalInit.headers);
    retryHeaders.delete('Authorization');
    const retryRes = await originalFetch(input, { ...finalInit, headers: retryHeaders });
    if (retryRes.status !== 401) return retryRes;
  }

  // 확정 401 → 세션 만료 공통 처리(토큰 삭제 + 로그인 리다이렉트).
  // auth/me 프로브는 라우터 가드가 처리하므로 여기선 토큰만 정리.
  if (res.status === 401) {
    if (isAuthMe) { try { localStorage.removeItem('token'); } catch {} }
    else handleSessionExpired();
  }
  return res;
};

// Combined Auth + Authorization guard with optimized caching (성능 최적화)
router.beforeEach(async (to, _from, next) => {
  if (!to.meta?.requiresAuth) return next();
  if (to.name === 'login') return next();

  const AUTH_CACHE_KEY = '__auth_check_cache';
  const AUTH_CACHE_TTL = 60000; // 60초 (30초 → 60초로 증가)

  // 캐시 확인
  const checkAuthCache = () => {
    try {
      const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
      if (!cached) return null;
      const { ok, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < AUTH_CACHE_TTL) return { ok };
      return null;
    } catch { return null; }
  };

  // 캐시 저장
  const saveAuthCache = (ok) => {
    try {
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({ ok, timestamp: Date.now() }));
    } catch {}
  };

  // 토큰이 있으면 인증된 것으로 간주 (빠른 경로)
  // 단, currentUser가 아직 채워지지 않았으면(예: 로그인 직후 첫 네비게이션이 겹치는 경우)
  // 빠른 경로를 건너뛰고 실제 프로브를 실행해 사용자 정보를 채운다.
  const token = localStorage.getItem('token');
  if (token) {
    const cachedAuth = checkAuthCache();
    console.log('[Auth Guard]', to.path, '- 캐시:', cachedAuth, 'currentUser:', currentUser.value?.Username || null);
    if (cachedAuth?.ok && currentUser.value) {
      console.log('[Auth Guard]', to.path, '- 캐시 사용, 통과');
      return next();
    }
  }
  console.log('[Auth Guard]', to.path, '- /api/auth/me 프로브 실행');

  const probe = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const r = await fetchWithTimeout('/api/auth/me', { credentials: 'include', headers }, 8000);

      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        // ok가 true이고, user 객체가 존재하며, user.id가 있어야 인증 성공
        if (j?.ok && j?.user && j.user.id) {
          return { ok: true, user: j.user };
        }
        return { ok: false, status: r.status };
      }

      return { ok: false, status: r.status };
    } catch (err) {
      // 타임아웃(AbortError)·네트워크 오류 → status 0 (진짜 401과 구분)
      return { ok: false, status: 0 };
    }
  };

  const result = await probe();
  console.log('[Auth Guard]', to.path, '- 프로브 결과:', result);

  if (result.ok && result.user) {
    // Store에 사용자 정보 저장 (UI 업데이트를 위해)
    updateUser(result.user);
    saveAuthCache(true);
    console.log('[Auth Guard]', to.path, '- 프로브 성공, currentUser 갱신:', result.user.username || result.user.Username);
    return next();
  }

  // 네트워크/타임아웃(status 0)인데 토큰이 있으면 세션은 살아있을 수 있음 → 로그아웃시키지 말고 낙관적 통과(화면 먹통 방지).
  //  · 세션이 실제로 죽었다면 해당 페이지의 API 호출이 401을 받아 handleSessionExpired가 로그인으로 보낸다.
  if (result.status === 0 && localStorage.getItem('token')) {
    console.warn('[Auth Guard]', to.path, '- 프로브 네트워크/타임아웃, 토큰 보유 → 낙관적 통과(로그아웃 안 함)');
    return next();
  }

  // 인증 실패(401 등) - 로그인 페이지로 리다이렉트
  sessionStorage.removeItem(AUTH_CACHE_KEY);
  try { await fetch('/api/auth/logout', { method:'POST', credentials:'include' }); } catch {}
  try { localStorage.removeItem('token'); sessionStorage.clear(); } catch {}

  // 모바일 브라우저에서 router.push가 실패하는 경우를 대비하여 강제 리다이렉트
  if (to.name === 'login') {
    console.log('[Router Guard] 이미 login 페이지로 이동 중, 통과');
    return next();
  }

  // 루트 경로(/)에서 오는 경우는 redirect 쿼리 생략 (URL 깔끔하게 유지)
  const redirectQuery = (to.fullPath === '/' || to.path === '/') ? {} : { redirect: to.fullPath };
  console.log('[Router Guard] 리다이렉트 쿼리:', redirectQuery);

  // 모바일에서 next()가 블로킹되는 경우를 대비하여 window.location 사용
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (isMobile) {
    console.log('[Router Guard] 모바일 감지, window.location으로 강제 리다이렉트');
    const redirectParam = Object.keys(redirectQuery).length > 0 ? `?redirect=${encodeURIComponent(redirectQuery.redirect)}` : '';
    window.location.href = `/login${redirectParam}`;
    return false; // 현재 navigation 취소
  }

  return next({ name: 'login', query: redirectQuery });
});

// Client-side event logging: disabled for performance optimization
router.afterEach((to, from) => {
  console.log('[Router] afterEach:', from.fullPath, '->', to.fullPath);
  // 네비게이션 로그 전송 비활성화 (성능 최적화)
});

// 이번 페이지 로드에서 메뉴 권한을 서버에서 한 번이라도 다시 읽었는지.
//  새로고침하면 다시 false 가 되므로, 첫 판정은 항상 최신 권한으로 한다.
let menuCheckedThisLoad = false;

// Authorization guard: block routes when user lacks required menu permission (with optimized caching)
router.beforeEach(async (to, _from, next) => {
  const req = to.meta?.requireAny;
  if (!req) return next(); // 권한 체크 불필요 - 즉시 통과

  const mapPath = (p) => {
    if (!p) return '/';
    if (p.endsWith('/index.html')) return '/';
    if (p.endsWith('/login.html')) return '/login';
    if (p.endsWith('/admin.html')) return '/admin';
    if (p.endsWith('/admin-logs.html')) return '/admin/logs';
    if (p.endsWith('/user-create.html')) return '/user-create';
    if (p.endsWith('/user-list.html')) return '/user-list';
    if (p.endsWith('/reports.html')) return '/reports';
    // 일반 .html 접미사 제거 (DB에 '.html'로 저장된 경로를 라우터 경로와 매칭)
    if (p.endsWith('.html')) return p.slice(0, -5);
    return p;
  };

  const decodeUid = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return 'me';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.sub ?? 'me';
    } catch { return 'me'; }
  };

  const uid = decodeUid();
  const cacheKey = `vite:allowed:${uid}`;
  const MENU_CACHE_TTL = 30 * 60 * 1000; // 30분 (성능 개선: 15분 → 30분)

  console.log('[Menu Guard]', to.path, '- uid:', uid, 'requireAny:', req);

  const loadAllowed = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : undefined;
      let prevAdmin = false;
      try { const cached = sessionStorage.getItem(cacheKey); if (cached) prevAdmin = !!(JSON.parse(cached)?.a); } catch {}
      const r = await fetchWithTimeout('/api/menu', { credentials: 'include', headers, cache: 'no-store' }, 8000);
      const j = await r.json().catch(() => null);
      if (!j?.ok) return { keys: new Set(), paths: new Set(), admin: false, timestamp: Date.now() };
      const keys = new Set();
      const paths = new Set();
      const arr = Array.isArray(j.menus) ? j.menus : [];
      for (const m of arr) {
        if (m?.Key) keys.add(String(m.Key));
        if (m?.Path) paths.add(mapPath(String(m.Path)));
      }
      const isAdminNow = !!j.admin;
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ k:[...keys], p:[...paths], a: isAdminNow, t: Date.now() })); } catch {}
      try { if (isAdminNow !== prevAdmin) { window.dispatchEvent(new CustomEvent('menu-permissions-changed', { detail: { adminChanged: true } })); } } catch {}
      console.log('[Menu Guard]', to.path, '- /api/menu 응답: keys=', [...keys], 'paths=', [...paths], 'admin=', isAdminNow);
      return { keys, paths, admin: isAdminNow, timestamp: Date.now() };
    } catch (e) {
      console.warn('[Menu Guard]', to.path, '- /api/menu 요청 실패:', e);
      // 네트워크/타임아웃 → 빈 권한으로 오탐 거부하지 말고, 기존 캐시(만료 포함)라도 재사용(화면 먹통/오거부 방지)
      try {
        const c = sessionStorage.getItem(cacheKey);
        if (c) { const o = JSON.parse(c); return { keys: new Set(o?.k || []), paths: new Set(o?.p || []), admin: !!o?.a, timestamp: o?.t || 0 }; }
      } catch {}
      return { keys: new Set(), paths: new Set(), admin: false, timestamp: Date.now() };
    }
  };

  let allowed;
  let cacheValid = false;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const obj = JSON.parse(cached);
      const age = Date.now() - (obj?.t || 0);
      cacheValid = age < MENU_CACHE_TTL;
      allowed = { keys: new Set(obj?.k || []), paths: new Set(obj?.p || []), admin: !!obj?.a, timestamp: obj?.t || 0 };
    }
  } catch {}

  // 페이지를 새로 연(새로고침) 뒤 첫 판정은 캐시를 건너뛰고 서버에서 다시 읽는다.
  //  sessionStorage 는 F5 로도 안 지워져서, 메뉴를 DB 에서 켜고 꺼도 30분 동안 반영되지 않았다.
  //  화면 안에서 오가는 동안은 그대로 캐시를 쓴다(매 이동마다 조회하지 않게).
  if (!menuCheckedThisLoad) {
    menuCheckedThisLoad = true;
    cacheValid = false;
  }

  console.log('[Menu Guard]', to.path, '- 캐시 상태:', cacheValid ? `유효 (age=${Date.now() - (allowed?.timestamp || 0)}ms)` : '없음/만료',
    allowed ? { keys: [...allowed.keys], paths: [...allowed.paths], admin: allowed.admin } : null);

  // 캐시가 유효하면 재검증 없이 사용 (성능 최적화)
  if (cacheValid && allowed) {
    // 관리자는 즉시 통과
    if (allowed.admin) { console.log('[Menu Guard]', to.path, '- 관리자, 통과'); return next(); }

    // 시스템 관리자 전용 라우트: 메뉴 권한 부여 여부와 무관하게 관리자만 허용
    if (to.meta?.adminOnly) {
      console.warn('[Menu Guard]', to.path, '- 관리자 전용 라우트, 비관리자 → denied=1');
      return next({ name: 'home', query: { denied: '1' } });
    }

    const requiresKeys = new Set([...(req.keys || [])].map(String));
    const requiresPaths = new Set([...(req.paths || [])].map(String));
    const okByKey = [...requiresKeys].some(k => allowed.keys.has(k));
    const okByPath = [...requiresPaths].some(p => allowed.paths.has(p));

    console.log('[Menu Guard]', to.path, '- (캐시) requiresKeys=', [...requiresKeys], 'okByKey=', okByKey, 'requiresPaths=', [...requiresPaths], 'okByPath=', okByPath);
    if (okByKey || okByPath) return next();
    // 권한 없음 - 즉시 거부 (API 호출 없음)
    console.warn('[Menu Guard]', to.path, '- 캐시 기준 권한 없음 → denied=1');
    return next({ name: 'home', query: { denied: '1' } });
  }

  // 캐시가 없거나 만료됨 - 새로 로드
  if (!allowed || !cacheValid) {
    allowed = await loadAllowed();
    // 관리자는 로드 후 즉시 통과
    if (allowed.admin) { console.log('[Menu Guard]', to.path, '- 관리자(신규 로드), 통과'); return next(); }
  }

  // 시스템 관리자 전용 라우트: 메뉴 권한 부여 여부와 무관하게 관리자만 허용
  if (to.meta?.adminOnly) {
    console.warn('[Menu Guard]', to.path, '- 관리자 전용 라우트(신규 로드), 비관리자 → denied=1');
    return next({ name: 'home', query: { denied: '1' } });
  }

  const requiresKeys = new Set([...(req.keys || [])].map(String));
  const requiresPaths = new Set([...(req.paths || [])].map(String));

  const okByKey = [...requiresKeys].some(k => allowed.keys.has(k));
  const okByPath = [...requiresPaths].some(p => allowed.paths.has(p));

  console.log('[Menu Guard]', to.path, '- (신규 로드) requiresKeys=', [...requiresKeys], 'okByKey=', okByKey, 'requiresPaths=', [...requiresPaths], 'okByPath=', okByPath);

  if (okByKey || okByPath) return next();

  // 권한 없음 - 접근 거부 (캐시 이미 로드됨)
  console.warn('[Menu Guard]', to.path, '- 신규 로드 기준 권한 없음 → denied=1');
  return next({ name: 'home', query: { denied: '1' } });
});

// Invalidate allowed cache when permissions are changed
window.addEventListener('menu-permissions-changed', () => {
  try{
    const token = localStorage.getItem('token');
    let uid = 'me';
    if (token) { try { uid = JSON.parse(atob(token.split('.')[1]))?.sub ?? 'me'; } catch {} }
    const cacheKey = `vite:allowed:${uid}`;
    sessionStorage.removeItem(cacheKey);
  }catch{}
});

// ── 배포 후 청크 해시가 바뀌면 옛 index.html이 없어진 청크를 404로 요청 →
//    "Failed to fetch dynamically imported module"로 라우트 이동이 조용히 실패(클릭해도 무반응).
//    이럴 때 한 번만 강제 새로고침하여 새 index.html/청크를 받아온다(무한 루프 방지 플래그).
const CHUNK_RELOAD_KEY = '__chunk_reload_at';
function reloadForStaleChunk() {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    // 10초 이내 재발이면(이미 새로고침했는데도 실패) 루프로 보고 중단
    if (Date.now() - last < 10000) { console.error('[Chunk] 새로고침 후에도 청크 로드 실패 - 배포 파일 확인 필요'); return; }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    console.warn('[Chunk] 오래된 청크 감지 → 새 버전 로드 위해 새로고침');
    window.location.reload();
  } catch { window.location.reload(); }
}
const isDynImportError = (msg) => /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically/i.test(String(msg || ''));

// Vite가 preload 실패 시 발생시키는 이벤트
window.addEventListener('vite:preloadError', (e) => { e.preventDefault?.(); reloadForStaleChunk(); });
// 라우터의 지연 컴포넌트 import 실패
router.onError((err) => { if (isDynImportError(err?.message)) reloadForStaleChunk(); });
// 혹시 놓친 케이스(프로미스 거부)
window.addEventListener('unhandledrejection', (e) => { if (isDynImportError(e?.reason?.message || e?.reason)) reloadForStaleChunk(); });

createApp(App).use(router).use(ElementPlus, { locale: ko })
  .directive('backdrop-close', backdropClose)   // 팝업 바깥 눌러 닫기(드래그로 닫히지 않게)
  .mount('#app');
